import { option } from 'fp-ts';

import { AppClockService, DateTimeService, DateTimeUtc, DateTimeUtcService } from '@mews/framework/datetime';
import { QuotaExceededError } from '@mews/framework/errors';

import { API_MAXIMUM_INTERVAL_TIME_UNITS } from '../../constants';
import { getValidQueryParamAvailabilityBlockId } from '../../selectors/views/availabilityBlocks';
import { ApplicationState, DistributorPromiseThunkAction } from '../../types';
import { WithCityId, WithEnterpriseId } from '../../types/selectors/selectorPropTypes';
import { isIntervalLimitExceeded } from '../../utils/intervalLimit';
import { getLanguageCode } from '../app/selectors';
import { hasAvailabilityBlocksFailed } from '../entities/availabilityBlocks/selectors';
import {
    getCityEnterprisesIds,
    getEnterpriseConfigurationId,
    getEnterpriseTimezone,
} from '../entities/enterprises/selectors';
import { getEnterpriseServiceId, getServiceTimeUnit, isServiceHourly } from '../entities/services/selectors';
import { getMissingAvailabilityDateRange } from './dateRanges';
import { getCombinedResponse, getCombinedRestrictions, getHourlyDateIntervals } from './hourlyUtils';

import {
    fetchServiceAvailabilityFailed,
    fetchServiceAvailabilityStarted,
    fetchServiceAvailabilitySucceeded,
} from './reducer';
import { getInitialAvailabilityInterval } from './selectors';
import { getServiceAvailabilityRequestId } from './utils';

export interface FetchServiceAvailabilityOptions {
    enterpriseId: string;
    startUtc: DateTimeUtc;
    endUtc: DateTimeUtc;
}

interface GetFetchData {
    state: ApplicationState;
    enterpriseId: string;
    startUtc: DateTimeUtc;
    endUtc: DateTimeUtc;
    appClockService: AppClockService;
}

const fetchInitialServiceAvailability =
    ({ enterpriseId }: WithEnterpriseId): DistributorPromiseThunkAction =>
    async (dispatch, getState, { appClockService, loggerService }) => {
        const state = getState();
        const nowUtc = appClockService.getAppNowUtc();
        const { startUtc, endUtc } = getInitialAvailabilityInterval(state, { enterpriseId, nowUtc });

        const isHourlyService = isServiceHourly(state, { enterpriseId });

        const fetchAvailabilityFunc = isHourlyService ? fetchHourlyServiceAvailability : fetchServiceAvailability;

        try {
            await dispatch(fetchAvailabilityFunc({ enterpriseId, startUtc, endUtc }));
        } catch (error) {
            loggerService.log('service-availability-fetch-failed', 'Service availability fetching failed.', {
                error,
                startUtc,
                endUtc,
                enterpriseId,
            });
        }
    };

export const fetchInitialCityServiceAvailabilities =
    ({ cityId }: WithCityId): DistributorPromiseThunkAction<void[]> =>
    async (dispatch, getState) => {
        const enterpriseIds = getCityEnterprisesIds(getState(), { cityId });
        const promises = enterpriseIds.map(async (enterpriseId) =>
            dispatch(fetchInitialServiceAvailability({ enterpriseId }))
        );
        return Promise.all(promises);
    };

const getFetchAvailabilityParams = ({ state, enterpriseId, startUtc, endUtc, appClockService }: GetFetchData) => {
    const serviceId = getEnterpriseServiceId(state, { enterpriseId });
    const configurationId = getEnterpriseConfigurationId(state, { enterpriseId });
    const timezone = getEnterpriseTimezone(state, { enterpriseId });
    const timeUnit = getServiceTimeUnit(state, { enterpriseId });

    if (timezone === undefined) {
        throw new TypeError('Timezone is undefined');
    }
    if (serviceId == null) {
        throw new TypeError('Service id is null');
    }
    if (configurationId == null) {
        throw new TypeError('Configuration id is null');
    }

    const languageCode = getLanguageCode(state);
    const nowUtc = appClockService.getAppNowUtc();
    const availabilityBlockId = getValidQueryParamAvailabilityBlockId(state, { nowUtc });
    const requestId = getServiceAvailabilityRequestId({ serviceId, startUtc, endUtc });
    const missingDateRange = getMissingAvailabilityDateRange(state, {
        enterpriseId,
        endUtc,
        serviceId,
        startUtc,
    });

    if (option.isNone(missingDateRange)) {
        return;
    }
    if (hasAvailabilityBlocksFailed(state)) {
        return;
    }

    const commonParams = {
        serviceId,
        enterpriseId,
        languageCode,
        fullAmounts: false,
        availabilityBlockId,
        bookingEngineId: configurationId,
    };

    return { timezone, serviceId, requestId, commonParams, missingDateRange, timeUnit };
};

export const fetchHourlyServiceAvailability =
    ({ enterpriseId, startUtc, endUtc }: FetchServiceAvailabilityOptions): DistributorPromiseThunkAction =>
    async (dispatch, getState, { apiService, appClockService, repositories }) => {
        const state = getState();

        const params = getFetchAvailabilityParams({
            state,
            enterpriseId,
            startUtc,
            endUtc,
            appClockService,
        });

        if (params == null) {
            return;
        }

        const { serviceId, timezone, requestId, commonParams, missingDateRange } = params;

        const missingEndUtc = DateTimeUtcService.parse(missingDateRange.value.endUtc.value);
        const missingStartUtc = DateTimeUtcService.parse(missingDateRange.value.startUtc.value);

        const intervals = getHourlyDateIntervals({
            startUtc: missingStartUtc,
            endUtc: missingEndUtc,
        });

        try {
            dispatch(fetchServiceAvailabilityStarted({ requestId }));

            const responsePromises = intervals.map(async (interval) =>
                apiService.getServicesAvailability({
                    endUtc: interval.end.value,
                    startUtc: interval.start.value,
                    ...commonParams,
                })
            );

            const restrictionsPromises = intervals.map(async (interval) =>
                repositories.restrictions.getAll({
                    serviceIds: [serviceId],
                    startUtc: interval.start.value,
                    endUtc: interval.end.value,
                })
            );

            const response = await Promise.all(responsePromises);
            const allRestrictions = await Promise.all(restrictionsPromises);
            const combinedResponse = getCombinedResponse(response);
            const combinedRestrictions = getCombinedRestrictions(
                allRestrictions.map(({ restrictions }) => restrictions)
            );

            dispatch(
                fetchServiceAvailabilitySucceeded({
                    enterpriseId,
                    serviceId,
                    requestId,
                    ...combinedResponse,
                    applicableRestrictions: combinedRestrictions,
                    restrictionsStartUtc: startUtc.value,
                    restrictionsEndUtc: endUtc.value,
                    isHourlyService: true,
                    timezone,
                })
            );
        } catch (error) {
            dispatch(fetchServiceAvailabilityFailed({ requestId }));
            throw error;
        }
    };

export const fetchServiceAvailability =
    ({ enterpriseId, startUtc, endUtc }: FetchServiceAvailabilityOptions): DistributorPromiseThunkAction =>
    async (dispatch, getState, { apiService, appClockService, loggerService, repositories }) => {
        const state = getState();
        const params = getFetchAvailabilityParams({
            state,
            enterpriseId,
            startUtc,
            endUtc,
            appClockService,
        });

        if (!params) {
            return;
        }
        const { serviceId, timezone, requestId, missingDateRange, commonParams, timeUnit } = params;

        const startDate = DateTimeService.toDate(DateTimeService.fromUtc(startUtc, timezone));
        const endDate = DateTimeService.toDate(DateTimeService.fromUtc(endUtc, timezone));

        if (isIntervalLimitExceeded(startDate.value, endDate.value, timeUnit)) {
            const maxIntervalError = new QuotaExceededError(
                `The interval must not exceed ${API_MAXIMUM_INTERVAL_TIME_UNITS} timeUnits. startDate: ${startUtc.value}, endDate: ${endUtc.value}`
            );
            loggerService.log(
                'fetch-availability-interval-exceeded',
                `The interval must not exceed ${API_MAXIMUM_INTERVAL_TIME_UNITS} timeUnits.`,
                { error: maxIntervalError }
            );
        }

        try {
            dispatch(fetchServiceAvailabilityStarted({ requestId }));

            const [response, { restrictions: applicableRestrictions }] = await Promise.all([
                apiService.getServicesAvailability({
                    endUtc: missingDateRange.value.endUtc.value,
                    startUtc: missingDateRange.value.startUtc.value,
                    ...commonParams,
                }),
                repositories.restrictions.getAll({
                    serviceIds: [serviceId],
                    startUtc: startUtc.value,
                    endUtc: endUtc.value,
                }),
            ]);

            dispatch(
                fetchServiceAvailabilitySucceeded({
                    enterpriseId,
                    serviceId,
                    requestId,
                    ...response,
                    applicableRestrictions,
                    restrictionsStartUtc: startUtc.value,
                    restrictionsEndUtc: endUtc.value,
                    timezone,
                })
            );
        } catch (error) {
            dispatch(fetchServiceAvailabilityFailed({ requestId }));
            throw error;
        }
    };
